import {
  Button,
  Checkbox,
  Container,
  IconSpecificProps,
  Spinner,
  SuccessIcon,
  Typography
} from '@ag1/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiRoutes } from '@portal/constants/routes';
import PageHeaderFooterLayout from '@portal/shared/layouts/PageHeaderFooterLayout';
import { CustomPortableText } from '@portal/shared/studio/PortableText';
import { StudioBaseImageV2 } from '@portal/shared/studio/StudioBaseImage/StudioBaseImage';
import IconX from '@portal/shared/ui/Icon/24/IconX';
import { IconLogoAG1 } from '@portal/shared/ui/Icon/picture/IconLogoAG1';
import InputV2 from '@portal/shared/ui/InputV2';
import { showCustomNotification } from '@portal/shared/ui/Notification';
import { fetcher } from '@portal/shared/utils/fetcher';
import { getISOLanguage } from '@portal/shared/utils/locale';
import { useStoreValue } from '@portal/state/hooks/useStore';
import clsx from 'clsx';
import { ElementType, useCallback, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import styles from './EmailCapturePage.module.scss';
import { EmailCapturePageProps, formSchema, FormValue } from './schema';

const successIconComponent: ElementType<IconSpecificProps> = () => <SuccessIcon size={24} />;

export const EmailCapturePage = ({ pageData, headerData, footerData }: EmailCapturePageProps) => {
  const store = useStoreValue();
  const [isLoading, setIsLoading] = useState(false);

  const {
    isAgzVariant,
    emailCaptureKlaviyoListId,
    emailCapturePageBody,
    emailCapturePageHeadline,
    emailCapturePageHeaderImage,
    emailCapturePageImage,
    emailCapturePageEmailText,
    emailCapturePageConsentText,
    emailCaptureSubmitText,
    emailCaptureSubmitSuccessText,
    emailCaptureSubmitErrorText,
    seoTitle
  } = pageData;

  const {
    handleSubmit,
    control,
    resetField,
    formState: { errors, isValid, isDirty }
  } = useForm<FormValue>({
    mode: 'onChange',

    defaultValues: {
      email: '',
      email_consent: false
    },

    resolver: zodResolver(formSchema)
  });

  const onSubmit: SubmitHandler<FormValue> = useCallback(
    async (data) => {
      try {
        setIsLoading(true);

        if (!emailCaptureKlaviyoListId) {
          throw new Error('Invalid list ID');
        }

        const { email } = data;
        const language = getISOLanguage(store);

        const eventPayload = {
          email,
          updatedListId: emailCaptureKlaviyoListId
        };

        await fetcher.post(apiRoutes.klaviyo.subscribe({ store, language }), eventPayload);

        resetField('email');
        resetField('email_consent');

        showCustomNotification({
          type: 'success',
          props: {
            title: emailCaptureSubmitSuccessText,
            icon: successIconComponent
          },
          toastOptions: {
            autoClose: 2000
          }
        });
      } catch (err) {
        showCustomNotification({
          type: 'error',
          props: {
            title: emailCaptureSubmitErrorText,
            icon: IconX
          },
          toastOptions: {
            autoClose: 2000
          }
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      store,
      emailCaptureKlaviyoListId,
      resetField,
      emailCaptureSubmitSuccessText,
      emailCaptureSubmitErrorText
    ]
  );

  const handleSubmitInternal = handleSubmit((e) => {
    onSubmit(e);
  });

  return (
    <PageHeaderFooterLayout title={seoTitle || ''} headerData={headerData} footerData={footerData}>
      <header className={clsx(styles.header, isAgzVariant && styles.agzHeader)}>
        {emailCapturePageHeaderImage?.image?.asset ? (
          <StudioBaseImageV2
            sizes="100%"
            image={emailCapturePageHeaderImage.image}
            tabletImage={emailCapturePageHeaderImage.tabletImage}
            mobileImage={emailCapturePageHeaderImage.mobileImage}
            className={styles.headerLogo}
          />
        ) : (
          <IconLogoAG1 className={styles.headerLogo} />
        )}
      </header>
      <Container className={clsx(isAgzVariant && styles.agzContainer)}>
        <main className={styles.content}>
          {emailCapturePageImage?.image?.asset && (
            <div className={styles.landerImageContainer}>
              <StudioBaseImageV2
                sizes="100%"
                image={emailCapturePageImage.image}
                tabletImage={emailCapturePageImage.tabletImage}
                mobileImage={emailCapturePageImage.mobileImage}
                className={styles.landerImage}
              />
            </div>
          )}
          <Typography variant="headline" size="2" noPadding>
            {emailCapturePageHeadline}
          </Typography>
          <Typography variant="body" size="1" noPadding>
            {emailCapturePageBody}
          </Typography>

          <form onSubmit={handleSubmitInternal} className={styles.form}>
            <Controller
              name="email"
              control={control}
              render={({ field: { name, ...field } }) => (
                <InputV2
                  {...field}
                  name={name}
                  className={styles.emailInput}
                  label={emailCapturePageEmailText}
                  errors={errors}
                  aria-invalid={!!errors.email}
                  inputMode="email"
                  autoComplete="email"
                  dataAutofillMonitor
                />
              )}
            />

            <Controller
              name="email_consent"
              control={control}
              render={({ field: { name, value, ...field } }) => (
                <Checkbox
                  {...field}
                  id={name}
                  name={name}
                  checked={value}
                  label={
                    <CustomPortableText
                      value={emailCapturePageConsentText}
                      className={styles.consentText}
                    />
                  }
                  colorVariant={isAgzVariant ? 'agz' : 'default'}
                />
              )}
            />

            <div className={styles.buttonContainer}>
              <Button
                variant={isAgzVariant ? 'agzPrimary' : 'primary'}
                type="submit"
                rounded
                fluid
                disabled={isLoading || !isValid || !isDirty}
                iconPosition="right"
                size="medium">
                {isLoading ? (
                  <Spinner color="brand-white" size={24} withOverlay={false} />
                ) : (
                  emailCaptureSubmitText
                )}
              </Button>
            </div>
          </form>
        </main>
      </Container>
    </PageHeaderFooterLayout>
  );
};

export default EmailCapturePage;
